import { Link, useParams } from 'react-router-dom';
import { FunctionSheetView } from '@/components/FunctionSheetView';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import {
  useAttachmentDownload,
  useEvent,
  useEventAttachments,
  useEventFunctionSheet,
} from '@/hooks/useDesk';
import { formatDate } from '@/lib/format';
import { formatEventPlace } from '@/types/desk';

function formatBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const event = useEvent(id);
  const sheet = useEventFunctionSheet(id);
  const attachments = useEventAttachments(id);
  const download = useAttachmentDownload();

  async function onDownload(attachmentId: string) {
    const res = await download.mutateAsync(attachmentId);
    window.open(res.url, '_blank', 'noopener,noreferrer');
  }

  function onPrintSheet() {
    window.print();
  }

  return (
    <div>
      <PageHeader
        title={event.data?.name || 'Event'}
        description="Venue booking detail, function sheet, and attachments."
        actions={
          <div className="flex flex-wrap gap-2 print:hidden">
            {sheet.data?.functionSheet ? (
              <button
                type="button"
                className="rounded-lg bg-dome-brass px-3 py-2 text-sm font-medium text-dome-ink"
                onClick={onPrintSheet}
              >
                Print function sheet
              </button>
            ) : null}
            <Link
              to="/events"
              className="rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm"
            >
              Back to events
            </Link>
          </div>
        }
      />

      {event.isLoading ? (
        <p className="animate-pulse-soft text-sm text-[var(--muted)]">Loading…</p>
      ) : null}
      {event.isError ? (
        <p className="text-sm text-dome-danger">
          {(event.error as Error).message}
        </p>
      ) : null}

      {event.data ? (
        <div className="space-y-4">
          <section className="rounded-xl border border-[var(--border)] bg-white/80 p-5 print:hidden">
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={event.data.status} />
              {event.data.source === 'cimso' ? (
                <span className="rounded-md bg-dome-forest/10 px-2 py-0.5 text-xs text-dome-forest">
                  Cimso
                  {event.data.cimso_booking_id != null
                    ? ` · booking ${event.data.cimso_booking_id}`
                    : ''}
                </span>
              ) : null}
              <span className="text-sm text-[var(--muted)]">
                {formatDate(event.data.starts_at)}
                {event.data.ends_at
                  ? ` → ${formatDate(event.data.ends_at)}`
                  : ''}
              </span>
            </div>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[var(--muted)]">Venue / room</dt>
                <dd className="font-medium">{formatEventPlace(event.data)}</dd>
              </div>
              <div>
                <dt className="text-[var(--muted)]">Client</dt>
                <dd>{event.data.domeec_clients?.company_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-[var(--muted)]">Guests</dt>
                <dd>{event.data.guest_count ?? '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[var(--muted)]">Notes</dt>
                <dd className="whitespace-pre-wrap">
                  {event.data.notes || '—'}
                </dd>
              </div>
            </dl>
            {event.data.source === 'cimso' ? (
              <p className="mt-3 text-xs text-[var(--muted)]">
                Cimso-sourced fields are read-only unless locally overridden.
                Dome never pushes changes back to Cimso.
              </p>
            ) : null}
          </section>

          {sheet.data?.functionSheet ? (
            <div className="print:block">
              <FunctionSheetView
                sheet={sheet.data.functionSheet}
                eventName={event.data.name}
                readOnly={sheet.data.cimsoFieldsReadOnly}
              />
            </div>
          ) : sheet.isLoading ? (
            <p className="text-sm text-[var(--muted)] print:hidden">
              Loading function sheet…
            </p>
          ) : event.data.source === 'cimso' ? (
            <p className="text-sm text-[var(--muted)] print:hidden">
              No function sheet stored yet — run Sync from Cimso in Settings.
            </p>
          ) : null}

          {(event.data.domeec_event_rooms || []).length > 0 ? (
            <section className="rounded-xl border border-[var(--border)] bg-white/80 p-5 print:hidden">
              <h2 className="font-display text-lg text-dome-forest">
                Room bookings
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                {event.data.domeec_event_rooms!.map((er) => (
                  <li
                    key={er.id}
                    className="flex flex-wrap justify-between gap-2 border-b border-[var(--border)] py-2 last:border-0"
                  >
                    <span>
                      {er.domeec_rooms?.name || er.room_id}
                      {er.layout ? ` · ${er.layout}` : ''}
                      {er.setup ? ` · setup ${er.setup}` : ''}
                    </span>
                    <span className="text-[var(--muted)]">
                      {formatDate(er.starts_at)}
                      {er.pax != null ? ` · ${er.pax} pax` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="rounded-xl border border-[var(--border)] bg-white/80 p-5 print:hidden">
            <h2 className="font-display text-lg text-dome-forest">
              Attachments
            </h2>
            {attachments.isLoading ? (
              <p className="mt-2 text-sm text-[var(--muted)]">Loading…</p>
            ) : null}
            {attachments.isError ? (
              <p className="mt-2 text-sm text-[var(--muted)]">
                Attachments unavailable (apply attachments migration).
              </p>
            ) : null}
            {!attachments.isLoading &&
            (attachments.data || []).length === 0 ? (
              <p className="mt-2 text-sm text-[var(--muted)]">
                No files linked to this event yet.
              </p>
            ) : null}
            {(attachments.data || []).length > 0 ? (
              <ul className="mt-3 divide-y divide-[var(--border)] text-sm">
                {attachments.data!.map((a) => (
                  <li
                    key={a.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                  >
                    <div>
                      <p className="font-medium">{a.filename}</p>
                      <p className="text-xs text-[var(--muted)]">
                        {a.mime || 'file'} · {formatBytes(a.size_bytes)}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded-lg bg-dome-forest px-3 py-1.5 text-sm text-dome-sand disabled:opacity-50"
                      disabled={download.isPending}
                      onClick={() => void onDownload(a.id)}
                    >
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        </div>
      ) : null}
    </div>
  );
}
